import { CheckCircle2, Clock } from "lucide-react"
import { useTranslation } from "react-i18next"
import {
  FleetReminderWorkflow,
  FleetReminderEntry,
  FleetReminderDue,
} from "@/reducers/openclawWorkflowsSlice"
import { cn } from "@/lib/utils"
import { getIntlLocale } from "@/lib/i18n/format"

// ─── helpers ─────────────────────────────────────────────────────────────────

type EntryStatus = "accepted" | "pending" | "upcoming"

function entryStatus(entry: FleetReminderEntry): EntryStatus {
  if (!entry.fired) return "upcoming"
  if (!entry.fired.acceptance) return "pending"
  return "accepted"
}

const SOON_DAYS = 30

/** Resolve the relevant date for a due descriptor relative to `today`. */
function resolveDate(due: FleetReminderDue, today: Date): Date {
  if (due.kind === "yearly") {
    const year = today.getFullYear()
    const candidate = new Date(year, due.month - 1, due.day)
    return candidate < today
      ? new Date(year + 1, due.month - 1, due.day)
      : candidate
  }
  return new Date(due.date)
}

function formatDueDate(due: FleetReminderDue, today: Date): string {
  const d = resolveDate(due, today)
  return d.toLocaleDateString(getIntlLocale(undefined), {
    day: "numeric",
    month: "short",
  })
}

function isSoon(due: FleetReminderDue, today: Date): boolean {
  const diffMs = resolveDate(due, today).getTime() - today.getTime()
  return diffMs <= SOON_DAYS * 24 * 60 * 60 * 1000
}

function sortEntries(
  entries: FleetReminderEntry[],
  today: Date,
): FleetReminderEntry[] {
  return [...entries].sort(
    (a, b) =>
      resolveDate(a.due, today).getTime() - resolveDate(b.due, today).getTime(),
  )
}

// ─── sub-components ──────────────────────────────────────────────────────────

const StatusIcon = ({ status }: { status: EntryStatus }) => {
  const { t } = useTranslation()
  if (status === "accepted") {
    return (
      <CheckCircle2
        className="h-3.5 w-3.5 shrink-0 text-emerald-500"
        strokeWidth={2}
        aria-label={t("ai.fleetReminder.acknowledged")}
      />
    )
  }
  if (status === "pending") {
    return (
      <Clock
        className="h-3.5 w-3.5 shrink-0 text-amber-400"
        strokeWidth={2}
        aria-label={t("ai.fleetReminder.pending")}
      />
    )
  }
  return <span className="h-3.5 w-3.5 shrink-0" aria-hidden />
}

const EntryRow = ({
  entry,
  today,
}: {
  entry: FleetReminderEntry
  today: Date
}) => {
  // Subscribes to language changes, so the due date re-renders on a switch.
  useTranslation()
  const status = entryStatus(entry)
  const isInactive = entry.status !== "active"
  const dateStr = formatDueDate(entry.due, today)

  // Is the due date today?
  const next = resolveDate(entry.due, today)
  const isToday =
    next.getFullYear() === today.getFullYear() &&
    next.getMonth() === today.getMonth() &&
    next.getDate() === today.getDate()

  return (
    <div
      className={cn(
        "flex items-center gap-2 py-0.5",
        isInactive && "opacity-40",
      )}
    >
      {/* plate */}
      <span className="min-w-0 flex-1 truncate font-mono text-xs text-foreground">
        {entry.plate}
      </span>

      {/* due date */}
      <span
        className={cn(
          "shrink-0 text-xs tabular-nums",
          isToday
            ? "font-semibold text-amber-500 dark:text-amber-400"
            : "text-muted-foreground",
        )}
      >
        {dateStr}
      </span>

      {/* status icon */}
      <StatusIcon status={status} />
    </div>
  )
}

// ─── public component ────────────────────────────────────────────────────────

/**
 * Renders a single `<fleet-reminder>` block (name + all entries) inside the
 * Automations sidebar.
 */
const FleetReminderSection = ({
  reminder,
  today = new Date(),
}: {
  reminder: FleetReminderWorkflow
  /** Injected for tests; defaults to `new Date()` */
  today?: Date
}) => {
  const { t } = useTranslation()
  const activeEntries = reminder.entries.filter(
    (e) => e.status === "active" && isSoon(e.due, today),
  )
  const sorted = sortEntries(activeEntries, today)

  return (
    <section>
      {/* header row */}
      <div className="mb-1 flex items-center gap-1.5">
        <p className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
          {reminder.name}
        </p>
        {/* active indicator dot */}
        {reminder.status === "active" && (
          <span
            className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500"
            title={t("ai.fleetReminder.active")}
          />
        )}
      </div>

      <div className="flex flex-col pl-4">
        {sorted.map((entry) => (
          <EntryRow key={entry.plate} entry={entry} today={today} />
        ))}
      </div>
    </section>
  )
}

export default FleetReminderSection
